/**
 * InstallAppButton — "Add to Home Screen" / PWA install affordance.
 *
 * Behaviour
 * ──────────
 * • On Android / desktop Chrome / Edge: captures the `beforeinstallprompt`
 *   event the moment the browser deems the app installable. We then surface
 *   a small fuchsia "Install app" button. Clicking it calls `prompt()` and
 *   dismisses itself if the user accepts/dismisses.
 * • On iOS Safari (which deliberately doesn't fire `beforeinstallprompt`):
 *   we detect iOS + non-standalone mode and render a "Add to Home Screen"
 *   button that opens an instruction sheet ("Tap Share → Add to Home Screen").
 * • If the app is already running standalone (display-mode:standalone) we
 *   render nothing — they've already installed.
 * • The user's "Not now" choice is remembered for 14 days via localStorage.
 */

import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { Download, X, Share2, Plus, Smartphone } from 'lucide-react';
import { Button } from './ui/button';

const SNOOZE_KEY = 'sl_install_snoozed_until';

const isIos = () => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  // iPhone, iPad, iPod  + iPad on iOS 13+ which masquerades as Mac.
  const iOS = /iPad|iPhone|iPod/.test(ua);
  const iPadOS = ua.includes('Mac') && navigator.maxTouchPoints > 1;
  return iOS || iPadOS;
};

const isStandalone = () => {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia?.('(display-mode: standalone)')?.matches ||
    window.navigator.standalone === true
  );
};

const InstallAppButton = ({ variant = 'compact' }) => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showIosSheet, setShowIosSheet] = useState(false);
  const [snoozed, setSnoozed] = useState(false);
  const standalone = isStandalone();
  const ios = isIos();

  useEffect(() => {
    try {
      const until = Number(localStorage.getItem(SNOOZE_KEY) || '0');
      if (until && Date.now() < until) setSnoozed(true);
    } catch {/* noop */}

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onInstalled = () => {
      setDeferredPrompt(null);
      try { localStorage.removeItem(SNOOZE_KEY); } catch {/* noop */}
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const snooze = () => {
    try {
      localStorage.setItem(
        SNOOZE_KEY,
        String(Date.now() + 14 * 24 * 60 * 60 * 1000),
      );
    } catch {/* noop */}
    setSnoozed(true);
  };

  const triggerInstall = async () => {
    // iOS Safari never fires `beforeinstallprompt`, so there's no native
    // dialog to surface — we always show the manual A2HS instructions sheet.
    // We check ios FIRST (rather than after the deferredPrompt branch) so a
    // weird state where both deferredPrompt and ios are true (rare; iPad in
    // desktop mode) still shows the iOS instructions, which is what users
    // actually need on Apple devices.
    if (ios) {
      setShowIosSheet(true);
      return;
    }
    if (deferredPrompt) {
      try {
        deferredPrompt.prompt();
        const choice = await deferredPrompt.userChoice;
        if (choice?.outcome !== 'accepted') {
          setDeferredPrompt(null);
        } else {
          setDeferredPrompt(null);
        }
      } catch {
        setDeferredPrompt(null);
      }
      return;
    }
    // Last resort — neither iOS nor a deferred prompt available. Show the
    // generic instructions sheet so the click never feels broken.
    setShowIosSheet(true);
  };

  // Render the modal into <body> so it can't be hidden by a parent's
  // stacking context (the AppShell header has filters/blur/transform on
  // it, which would otherwise trap the fixed-positioned sheet underneath).
  const portalRoot = typeof document !== 'undefined' ? document.body : null;
  const iosSheet = showIosSheet ? (
    <IosInstallSheet onClose={() => setShowIosSheet(false)} onSnooze={snooze} />
  ) : null;

  // Already installed → render nothing.
  if (standalone) return null;
  // Snoozed for 14 days → render nothing.
  if (snoozed) return null;
  // No way to install on this browser → render nothing.
  if (!deferredPrompt && !ios) return null;

  const cls =
    variant === 'compact'
      ? 'text-xs px-2.5 py-1.5 rounded-lg font-semibold flex items-center gap-1.5'
      : 'w-full text-sm px-4 py-3 rounded-lg font-semibold flex items-center gap-2';

  return (
    <>
      <button
        onClick={triggerInstall}
        className={`${cls} bg-fuchsia-600/90 text-white hover:bg-fuchsia-700 transition-colors`}
        data-testid="install-app-btn"
        title="Add Smarter Learning Hub to your home screen for one-tap launch."
      >
        <Download className="w-3.5 h-3.5" />
        <span>Install app</span>
      </button>

      {portalRoot && iosSheet ? ReactDOM.createPortal(iosSheet, portalRoot) : iosSheet}
    </>
  );
};

const IosInstallSheet = ({ onClose, onSnooze }) => (
  <div
    className="fixed inset-0 z-[120] flex items-end sm:items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
    onClick={onClose}
    data-testid="install-ios-sheet"
  >
    <div
      className="w-full max-w-md bg-slate-900 border border-fuchsia-500/40 rounded-2xl shadow-[0_0_60px_-15px_rgba(217,70,239,0.45)] overflow-hidden"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-start justify-between gap-3 p-5 border-b border-slate-700">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-xl bg-fuchsia-600/30 border border-fuchsia-400/40 flex items-center justify-center shrink-0">
            <Smartphone className="w-5 h-5 text-fuchsia-200" />
          </div>
          <div className="min-w-0">
            <h3 className="text-base font-bold text-white">
              Add to Home Screen
            </h3>
            <p className="text-xs text-gray-400">
              One-tap launch · works offline
            </p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-1.5 rounded-md text-gray-400 hover:text-white hover:bg-slate-800"
          aria-label="Close"
          data-testid="install-ios-sheet-close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="p-5 space-y-4">
        <p className="text-sm text-gray-300 leading-relaxed">
          On iPhone & iPad you install web apps from Safari's share menu.
          Follow these two steps:
        </p>

        <ol className="space-y-3">
          <li className="flex items-start gap-3">
            <span className="w-7 h-7 shrink-0 rounded-full bg-fuchsia-600 text-white text-sm font-bold flex items-center justify-center">
              1
            </span>
            <div className="flex-1 text-sm text-gray-200">
              Tap the{' '}
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-slate-800 border border-slate-700">
                <Share2 className="w-3.5 h-3.5 text-blue-300" />
                Share
              </span>{' '}
              button at the bottom of Safari.
            </div>
          </li>
          <li className="flex items-start gap-3">
            <span className="w-7 h-7 shrink-0 rounded-full bg-fuchsia-600 text-white text-sm font-bold flex items-center justify-center">
              2
            </span>
            <div className="flex-1 text-sm text-gray-200">
              Scroll down and pick{' '}
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-slate-800 border border-slate-700">
                <Plus className="w-3.5 h-3.5 text-emerald-300" />
                Add to Home Screen
              </span>
              . The icon will land on your home screen and open like a
              native app.
            </div>
          </li>
        </ol>

        <p className="text-[11px] text-gray-500">
          Tip: while running from the home screen the app stays signed in
          and works on flaky connections.
        </p>

        <div className="flex gap-2 pt-1">
          <Button
            variant="outline"
            onClick={() => {
              onSnooze();
              onClose();
            }}
            className="flex-1 text-gray-300 border-slate-600"
            data-testid="install-ios-sheet-snooze"
          >
            Not now
          </Button>
          <Button
            onClick={onClose}
            className="flex-[2] bg-fuchsia-600 hover:bg-fuchsia-700 text-white"
            data-testid="install-ios-sheet-ok"
          >
            Got it
          </Button>
        </div>
      </div>
    </div>
  </div>
);

export default InstallAppButton;
